import Vue from 'vue';
import Vuex, {StoreOptions} from 'vuex';
import {NAVI_SERVICE_INSTANCE} from '@/services/navi.service';
import {ERROR_SERVICE_INSTANCE} from '@/services/error.service';
import {ROUTER_SERVICE} from '@/services/router.service';
import vuetify from './plugins/vuetify';
import {findIndex} from 'lodash';

Vue.use(Vuex);

export type SnackColorType = 'error' | 'warning' | 'info' | 'primary' | 'secondary' | 'accent' | 'success';

export interface SnackType {
    message: string;
    color?: SnackColorType;
}

interface RootState {
    navigation: {
        show: boolean;
        isLocked: boolean;
        routeRerenderCounter: number
    };
    route: {
        lastRouteSettings: { [key: string]: string };
    };
    theme: {
        dark: boolean;
    };
    companyAndUserIndicator: {
        locked: boolean;
    };
    companyChange: {
        show: boolean;
    };
    naviSearch: {
        show: boolean;
    };
    user: DisguiseDto.UserDataDto;
    snack: SnackType;
    naviFavorites: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>;
    navi: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>;
    submenuEntries: DisguiseDto.NaviSearchResultDto[];
    superUser: boolean;
    naviSearchEnabled: boolean;
    documentationEnabled: boolean;
    naviTreeKey: number;
    hideNavi: boolean;
}

const storeOptions: StoreOptions<RootState> = {
    state: {
        navigation: {
            show: true,
            isLocked: false,
            routeRerenderCounter: 0
        },
        route: {
            lastRouteSettings: {}
        },
        theme: {
            dark: false
        },
        companyAndUserIndicator: {
            locked: false
        },
        companyChange: {
            show: false
        },
        naviSearch: {
            show: false
        },
        user: {},
        snack: {
            message: ''
        },
        naviFavorites: [],
        navi: [],
        submenuEntries: [],
        superUser: false,
        naviSearchEnabled: false,
        documentationEnabled: false,
        naviTreeKey: 0,
        hideNavi: false
    },
    mutations: {// sync
        /**
         * Triggers a force refresh of the current route
         * You will lose all component states and the underlying route will be reloaded (hard reload)
         */
        rerenderRouterView(state) {
            state.navigation.routeRerenderCounter += 1;
        },
        toggleShowNavigation(state) {
            state.navigation.show = !state.navigation.show;
        },
        toggleNavigationLock(state) {
            state.navigation.isLocked = !state.navigation.isLocked;
        },
        showNavigation(state) {
            state.navigation.show = true;
        },
        showCompanyChangeSearch(state) {
            state.companyChange.show = true;
        },
        hideCompanyChangeSearch(state) {
            state.companyChange.show = false;
        },
        showNaviSearch(state) {
            state.naviSearch.show = true;
        },
        hideNaviSearch(state) {
            state.naviSearch.show = false;
        },
        // Increments naviTreeKey to force a full re-render of the navigation tree.
        // This causes all NavigationEntryTemplate instances to re-run created(),
        // which recalculates routeMatched and correctly expands groups for the current route.
        refreshNaviTree(state) {
            state.naviTreeKey += 1;
        },
        hideNavigation(state) {
            if (state.navigation.isLocked) {
                return;
            }
            state.navigation.show = false;
        },
        toggleCompanyAndUserIndicatorLocked(state) {
            state.companyAndUserIndicator.locked = !state.companyAndUserIndicator.locked;
        },
        restoreStoreState(state) {
            // Check if the ID exists
            const LocalStorageEntry = localStorage.getItem('store');
            if (LocalStorageEntry) {
                try {
                    // Replace the state object with the stored item
                    const savedState = Object.assign(state, JSON.parse(LocalStorageEntry));
                    this.replaceState({
                        ...savedState,
                        companyChange: {
                            show: false
                        },
                        naviSearch: {
                            show: false
                        },
                        hideNavi: false
                    });
                } catch (error) {
                    alert(error);
                }
            }
        },
        toggleDarkTheme(state) {
            state.theme.dark = !state.theme.dark;
            if (state.theme.dark) {
                document.body.style.backgroundColor = '#353535';
            } else {
                document.body.style.backgroundColor = '#fff';
            }
            document.documentElement.style.setProperty('color-scheme', state.theme.dark ? 'dark' : 'light');
            vuetify.framework.theme.dark = state.theme.dark;
        },
        persistRouteSettings(state, payload: { currentActiveRoutePath: string, toBeRestoredUrl: string }) {
            state.route.lastRouteSettings[payload.currentActiveRoutePath] = payload.toBeRestoredUrl;
        },
        setUserInfo(state, payload: { userInfo: DisguiseDto.UserDataDto }) {
            state.user = payload.userInfo;
        },
        setNavi(state, payload: {
            navi: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>;
            superUser?: boolean;
            naviSearchEnabled?: boolean;
            documentationEnabled?: boolean;
        }) {
            state.navi = payload.navi;
            state.superUser = payload.superUser === true;
            state.naviSearchEnabled = payload.naviSearchEnabled === true;
            state.documentationEnabled = payload.documentationEnabled === true;
            store.commit('setNaviFavorites', {favorites: NAVI_SERVICE_INSTANCE.extractFavoriteList(payload.navi)});
        },
        setSubmenuEntries(state, payload: { entries: DisguiseDto.NaviSearchResultDto[] }) {
            state.submenuEntries = payload.entries;
        },
        showSnack(state, payload: SnackType) {
            state.snack = {
                message: payload.message,
                color: payload.color
            };
            if (payload.color === 'error') {
                ERROR_SERVICE_INSTANCE.reportError({
                    message: payload.message,
                    route: ROUTER_SERVICE.getInstance().currentRoute.fullPath,
                    timestamp: new Date().toISOString()
                });
            }
        },
        setNaviFavorites(state,
                         payload: {
                             favorites: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>
                         }) {
            state.naviFavorites = payload.favorites;
        },
        setNaviFavorite(state,
                        payload: {
                            favorite: DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>
                        }) {
            const clonedFavs = [...store.state.naviFavorites];
            clonedFavs.unshift(payload.favorite); // add as first entry
            store.commit('setNaviFavorites', {favorites: clonedFavs});
        },
        unsetNaviFavorite(state,
                          payload: {
                              favorite: DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>
                          }) {
            const clonedFavs = [...store.state.naviFavorites];
            const idx = findIndex(clonedFavs, {id: payload.favorite.id});
            if (idx >= 0) {
                clonedFavs.splice(idx, 1);
                store.commit('setNaviFavorites', {favorites: clonedFavs});
            }
        },
        setHideNavi(state, payload: boolean) {
            state.hideNavi = payload;
        }
    },
    actions: {// async
        reorderNaviFavorites({commit, state},
                             payload: {
                                 naviEntries: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>
                             }
        ):
            Promise<Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>> {
            return new Promise<Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>>(
                ((resolve, reject) => {
                    const convertedFavorites: DisguiseDto.NaviFavoriteDto[] = [];
                    for (const favorite of payload.naviEntries) {
                        convertedFavorites.push({
                            naviId: favorite.id
                        });
                    }
                    commit('setNaviFavorites', {favorites: payload.naviEntries});
                    NAVI_SERVICE_INSTANCE.updateFavoriteOrder(convertedFavorites).then(() => {
                        resolve(payload.naviEntries);
                    }, () => {
                        reject();
                    });
                }));
        },
        deleteNaviFavorite({commit, state},
                           payload: {
                               naviEntry: DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>
                           }
        ) {
            return new Promise(
                ((resolve, reject) => {
                    NAVI_SERVICE_INSTANCE.removeFavorite({
                        naviId: payload.naviEntry.id
                    }).then(() => {
                        commit('unsetNaviFavorite', {favorite: payload.naviEntry});
                        resolve(payload.naviEntry);
                    }, () => {
                        reject();
                    });
                }));
        },
        addNaviFavorite({commit, state},
                        payload: {
                            naviEntry: DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>
                        }
        ) {
            return new Promise(
                ((resolve, reject) => {
                    NAVI_SERVICE_INSTANCE.addFavorite({
                        naviId: payload.naviEntry.id
                    }).then(() => {
                        commit('setNaviFavorite', {favorite: payload.naviEntry});
                        resolve(payload.naviEntry);
                    }, () => {
                        reject();
                    });
                }));
        },
    },
    getters: {}
};

const store = new Vuex.Store(storeOptions);

// Subscribe to store updates
store.subscribe((mutation, state) => {
    // Store the state object as a JSON string
    localStorage.setItem('store', JSON.stringify(state));
});

export default store;
